import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import AthleteSearch from "./AthleteSearch";
import AthleteResultsList from "./AthleteResultsList";
import AthleteChip from "./AthleteChip";
import SearchFilters from "../../../components/SearchFilters";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { useAxiosInstance } from "../../../services/Interceptor";
import { SearchAthletesPagedAsync } from "../../../services/AthleteService";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { mergeAthleteFavouriteStatuses } from "../../../store/slices/athleteSlice";

const PAGE_SIZE = 20;

function mergeAthleteProfileIntoListItem(athlete, profile) {
  if (!athlete || !profile) return athlete;
  return {
    ...athlete,
    ...profile,
    name: profile.firstName ?? profile.name ?? athlete.name,
    lastname: profile.lastName ?? profile.lastname ?? athlete.lastname,
    position: profile.primaryPosition ?? profile.position ?? athlete.position,
    primaryPosition: profile.primaryPosition ?? athlete.primaryPosition,
    expectedYearOfGraduation:
      profile.expectedYearOfGraduation ?? athlete.expectedYearOfGraduation,
    highSchool: profile.highSchool ?? athlete.highSchool,
  };
}

export default function AthleteSearchPanel({
  authUser,
  selectedAthlete,
  onAthleteSelected,
  adjustedHeight,
  highSchool,
  club,
  showFilters = true,
  listVariant = "rows", // "rows" (admin "Athletes" page) | "chips" (coach dashboard, admin dashboard)
  chipProps = {},
  favoritesOnly = false,
  refreshKey = 0,
}) {
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedQ = useDebouncedValue(searchTerm, 250);

  const [appliedFilters, setAppliedFilters] = useState({
    year: [],
    position: [],
  });
  const [filterDraft, setFilterDraft] = useState({ year: [], position: [] });
  const [allSelectedFilters, setAllSelectedFilters] = useState([]);

  const [displayedAthletes, setDisplayedAthletes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const cacheRef = useRef(new Map());
  const observerTarget = useRef(null);

  // Memoize the filter arrays so their references only change when the underlying
  // selections change. Passes the full arrays to the server (which supports
  // multi-select via repeated query params); preserves the multi-value behavior
  // the old client-side filter logic supported.
  const positionFilters = useMemo(
    () => appliedFilters.position || [],
    [appliedFilters.position],
  );
  const gradYearFilters = useMemo(
    () => appliedFilters.year || [],
    [appliedFilters.year],
  );

  const fetchPage = useCallback(
    async (q, page, append) => {
      // Cache key uses sorted joins so {QB, RB} and {RB, QB} share the same entry.
      const positionKey = [...positionFilters].sort().join(",");
      const gradYearKey = [...gradYearFilters].sort().join(",");
      const key = `${q}|${positionKey}|${gradYearKey}|${highSchool || ""}|${club || ""}|${favoritesOnly ? "fav" : ""}|${page}`;
      const cached = cacheRef.current.get(key);
      if (cached) {
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...(cached.items || [])]);
        } else {
          setDisplayedAthletes(cached.items || []);
        }
        setHasMore(!!cached.hasMore);
        setCurrentPage(page);
        return;
      }

      const setBusy = append ? setIsLoadingMore : setLoading;
      setBusy(true);
      try {
        const data = await SearchAthletesPagedAsync(apiInstance, {
          q,
          page,
          pageSize: PAGE_SIZE,
          position: positionFilters,
          gradYear: gradYearFilters,
          highSchool,
          club,
          favoritesOnly,
        });
        cacheRef.current.set(key, data);
        const items = data.items || [];
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...items]);
        } else {
          setDisplayedAthletes(items);
        }
        dispatch(
          mergeAthleteFavouriteStatuses(
            items
              .filter((a) => a.userId != null)
              .map((a) => ({ userId: a.userId, liked: !!a.liked })),
          ),
        );
        setHasMore(!!data.hasMore);
        setCurrentPage(page);
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load athletes";
        toast.error(message, { position: "top-right" });
        if (!append) {
          setDisplayedAthletes([]);
          setHasMore(false);
        }
      } finally {
        setBusy(false);
      }
    },
    // apiInstance is stable: useAxiosInstance() memoizes the axios instance
    // with an empty deps array, so the reference doesn't change between renders.
    [
      apiInstance,
      dispatch,
      positionFilters,
      gradYearFilters,
      highSchool,
      club,
      favoritesOnly,
    ],
  );

  useEffect(() => {
    fetchPage(debouncedQ, 0, false);
  }, [
    debouncedQ,
    positionFilters,
    gradYearFilters,
    highSchool,
    club,
    favoritesOnly,
    fetchPage,
  ]);

  // Stash the latest "refresh action" in a ref every render so the refreshKey effect
  // below always invokes the current closure (with the current fetchPage + debouncedQ),
  // never a stale one. This avoids the eslint-disable-line trap and addresses the
  // stale-closure concern from review even though apiInstance is in fact stable.
  const refreshActionRef = useRef(null);
  refreshActionRef.current = () => {
    cacheRef.current.clear();
    fetchPage(debouncedQ, 0, false);
  };

  // When the parent bumps refreshKey (e.g., after a like changes elsewhere), drop the
  // cache and refetch page 0 so stale entries disappear.
  useEffect(() => {
    if (refreshKey === 0) return;
    refreshActionRef.current?.();
  }, [refreshKey]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoadingMore &&
          !loading
        ) {
          fetchPage(debouncedQ, currentPage + 1, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.unobserve(target);
  }, [hasMore, isLoadingMore, loading, currentPage, debouncedQ, fetchPage]);

  // Accepts either a synthetic event (the default from <AthleteSearch>'s <input onChange>)
  // or a raw string value. Older call sites in CoachDashboard passed the latter.
  const handleSearch = (eOrValue) => {
    const value =
      typeof eOrValue === "string" ? eOrValue : (eOrValue?.target?.value ?? "");
    setSearchTerm(value);
  };

  // When an admin edits an athlete's profile via AthleteChip, merge the updated
  // fields into the displayed list in place and invalidate the cache so future
  // page fetches return fresh data.
  const handleChipAthleteUpdate = useCallback((userId, updatedProfile) => {
    setDisplayedAthletes((prev) =>
      prev.map((a) =>
        a.userId === userId
          ? mergeAthleteProfileIntoListItem(a, updatedProfile)
          : a,
      ),
    );
    cacheRef.current.clear();
  }, []);

  const buildSelectedFilters = useCallback(
    (filters) => [
      ...(filters.year || []).map((year) => ({
        type: "year",
        value: `Year: ${year}`,
        original: year,
      })),
      ...(filters.position || []).map((position) => ({
        type: "position",
        value: `Position: ${position}`,
        original: position,
      })),
    ],
    [],
  );

  const handleApplyAthleteFilters = useCallback(
    (filters) => {
      const next = {
        year: filters.year || [],
        position: filters.position || [],
      };
      setAppliedFilters(next);
      setFilterDraft(next);
      setAllSelectedFilters(buildSelectedFilters(next));
    },
    [buildSelectedFilters],
  );

  const clearFilter = (filterToRemove) => {
    const updater = (prev) => {
      const u = { ...prev };
      if (filterToRemove.type === "year") {
        u.year = (u.year || []).filter(
          (y) => String(y) !== String(filterToRemove.original),
        );
      } else if (filterToRemove.type === "position") {
        u.position = (u.position || []).filter(
          (p) => p !== filterToRemove.original,
        );
      }
      return u;
    };
    setAppliedFilters(updater);
    setFilterDraft(updater);
  };

  const clearAll = () => {
    const reset = { year: [], position: [] };
    setAppliedFilters(reset);
    setFilterDraft(reset);
    setAllSelectedFilters([]);
  };

  useEffect(() => {
    setAllSelectedFilters(buildSelectedFilters(appliedFilters));
  }, [appliedFilters, buildSelectedFilters]);

  return (
    <>
      {showFilters ? (
        <SearchFilters
          searchContent={<AthleteSearch handleSearch={handleSearch} />}
          filterDraft={filterDraft}
          setFilterDraft={setFilterDraft}
          appliedFilters={appliedFilters}
          onApplyFilters={handleApplyAthleteFilters}
          allSelectedFilters={allSelectedFilters}
          clearFilter={clearFilter}
          clearAll={clearAll}
        />
      ) : (
        <AthleteSearch handleSearch={handleSearch} />
      )}

      {loading ? (
        <div className="mt-5">
          <LoadingSpinner />
        </div>
      ) : listVariant === "chips" ? (
        <div
          className={`overflow-auto flex flex-col mt-2 pr-4 -mr-4 pb-24 lg:pb-0${adjustedHeight ? "" : " flex-1"}`}
          style={adjustedHeight ? { height: `${adjustedHeight}px` } : undefined}
        >
          {displayedAthletes.length > 0 ? (
            <>
              {displayedAthletes.map((athlete) => (
                <AthleteChip
                  key={athlete.userAthleteId || athlete.userId}
                  athlete={athlete}
                  showStarButton
                  {...chipProps}
                  onAthleteUpdate={(updatedProfile) =>
                    handleChipAthleteUpdate(athlete.userId, updatedProfile)
                  }
                />
              ))}
              <div ref={observerTarget} style={{ height: "1px" }} />
              {isLoadingMore && (
                <div className="text-center text-gray-500 text-sm py-3">
                  Loading more athletes...
                </div>
              )}
            </>
          ) : (
            <div className="w-full p-2 mt-5 flex flex-col items-center">
              <img
                alt="No athletes that match your search"
                src="/nodata.gif"
                className="object-cover h-20 w-20 rounded-full border-gray-300 border"
              />
              <span className="mt-4 text-center text-md text-slate-600">
                There are no athletes that match your search
              </span>
            </div>
          )}
        </div>
      ) : (
        <AthleteResultsList
          displayedAthletes={displayedAthletes}
          adjustedHeight={adjustedHeight}
          selectedAthlete={selectedAthlete}
          onSelectAthlete={onAthleteSelected}
          authUser={authUser}
          observerTarget={observerTarget}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
        />
      )}
    </>
  );
}
